import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Box, CircularProgress, Container, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { IRootReducerState } from 'state/reducers'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { checkPreconditionStatus } from '../state/actions'

interface ApiResponse {
  Data: {
    Preconditions: {
      length: number
      Creditworthiness: string
      Threestepcustomer: string
      Deliveroptions: string
      Packageexists: string
    }
    Status: string
    AdminStatus: string
  }
}

interface CustomerTabProps {
  customer: {
    CustEosId: string
    CustNo: string
    CustName: string
    Location: string
    Street: string
    ZipCode: string
    OuName: string
    ShipToLoc: string
    AppId: number
  }
  allowProceed: (allow: boolean) => void
  selectedPackage: { ModuleID: string; ModuleName: string } | null
  setPreconditionStatus: Dispatch<SetStateAction<ApiResponse | null>>
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    overflow: 'hidden'
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '0px',
    overflow: 'hidden',
    height: '100%',
    maxWidth: '70%'
  },
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: '0px',
    width: '100%'
  },
  tabPanel: {
    paddingLeft: '20px',
    paddingRight: '20px',
    paddingTop: '10px',
    paddingBottom: '10px',
    marginBottom: '5px'
  },
  sectionBox: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '10px',
    alignItems: 'flex-start'
  },
  sectionHeader: {
    marginBottom: '8px'
  },
  icon: {
    marginLeft: 'auto'
  }
})

const theme = createTheme()

const PreCondition: React.FC<CustomerTabProps> = props => {
  const { t } = useTranslation()
  const classes = useStyles()
  const customer = props.customer
  const allowProceed = props.allowProceed

  const supportURL = useSelector((state: IRootReducerState) => state.auth?.urls?.support)
  const tokenType = useSelector((state: IRootReducerState) => state.auth?.tokens?.TokenType)
  const tokenId = useSelector((state: IRootReducerState) => state.auth?.tokens?.IdToken)
  const ou_name = useSelector((state: IRootReducerState) => state.auth.user.ou_name)
  const AppId = useSelector((state: IRootReducerState) => state.auth.user.AppId)
  const userRoles = useSelector((state: IRootReducerState) => state.auth?.user?.['custom:role'])
  const [shouldProceed, setShouldProceed] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [apiResponse, setApiResponse] = useState<ApiResponse | null>(null)
  const dispatch = useDispatch()

  useEffect(() => {
    allowProceed(shouldProceed)
  }, [shouldProceed])

  useEffect(() => {
    const fetchPreconditions = async () => {
      setLoading(true)
      try {
        const response = (await dispatch(
          checkPreconditionStatus(customer.CustNo, customer.ShipToLoc, ou_name || '', String(AppId), customer.CustEosId)
        )) as unknown as ApiResponse

        if (response) {
          setApiResponse(response)
          props.setPreconditionStatus(response)
          const responseData = response.Data
          if (userRoles === 'dach_super_user' && responseData.AdminStatus === 'Success') {
            setShouldProceed(true)
          } else if (userRoles.includes('dach_super_user') && responseData.AdminStatus === 'Fail') {
            setShouldProceed(false)
          } else if (responseData.Status === 'Fail') {
            setShouldProceed(false)
          } else if (responseData.Status === 'Success') {
            setShouldProceed(true)
          }
        } else {
          setShouldProceed(false)
        }
      } catch (error) {
        console.error('Error:', error)
        setShouldProceed(false)
      } finally {
        setLoading(false)
      }
    }
    fetchPreconditions()
  }, [supportURL, tokenType, tokenId, userRoles, customer])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
        <CircularProgress size={20} />
      </Box>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.root}>
        <main className={classes.content}>
          <Container className={classes.mainContainer}>
            <div className={classes.tabPanel}>
              <Typography variant='h6' style={{ marginBottom: '16px', fontSize: 20, fontWeight: 700 }}>
                {t('AppLabels.preCondition')}
              </Typography>
              {apiResponse && apiResponse.Data && apiResponse.Data.Preconditions && Object.keys(apiResponse.Data.Preconditions).length > 0
                ? Object.entries(apiResponse.Data.Preconditions).map(([key, value]) => {
                    if (key === 'Creditworthiness') {
                      return (
                        <Box key={key} className={classes.sectionBox}>
                          <Box
                            style={{
                              display: 'flex',
                              width: '100%',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              borderRadius: '5px'
                            }}
                          >
                            {value === 'Success' ? (
                              <CheckCircleIcon color='success' className={classes.icon} style={{ width: '3%' }} />
                            ) : (
                              <CancelIcon
                                data-testid='CreditworthinessErrorIcon'
                                color='error'
                                className={classes.icon}
                                style={{ width: '3%' }}
                              />
                            )}
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: value === 'Fail' ? '#dc3545' : '#28a745',
                                paddingTop: 0.5,
                                paddingBottom: 0.5,
                                paddingLeft: 2,
                                paddingRight: 2
                              }}
                              style={{ width: '97%' }}
                            >
                              {value === 'Success' ? t('AppLabels.creditWorthinessOK') : t('AppLabels.creditWorthinessNotOK')}
                            </Typography>
                          </Box>
                        </Box>
                      )
                    } else if (key === 'Threestepcustomer') {
                      return (
                        <Box key={key} className={classes.sectionBox}>
                          <Box
                            style={{
                              display: 'flex',
                              width: '100%',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              borderRadius: '5px'
                            }}
                          >
                            {value === 'Success' ? (
                              <CheckCircleIcon color='success' className={classes.icon} style={{ width: '3%' }} />
                            ) : (
                              <CancelIcon
                                data-testid='ThreestepcustomerErrorIcon'
                                color='error'
                                className={classes.icon}
                                style={{ width: '3%' }}
                              />
                            )}
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: value === 'Fail' ? '#dc3545' : '#28a745',
                                paddingTop: 0.5,
                                paddingBottom: 0.5,
                                paddingLeft: 2,
                                paddingRight: 2
                              }}
                              style={{ width: '97%' }}
                            >
                              {value === 'Success' ? t('AppLabels.threeStepCustomerCheckOK') : t('AppLabels.threeStepCustomerCheckNotOK')}
                            </Typography>
                          </Box>
                        </Box>
                      )
                    } else if (key === 'Deliveroptions') {
                      return (
                        <Box key={key} className={classes.sectionBox}>
                          <Box
                            style={{
                              display: 'flex',
                              width: '100%',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              borderRadius: '5px'
                            }}
                          >
                            {value === 'Success' ? (
                              <CheckCircleIcon color='success' className={classes.icon} style={{ width: '3%' }} />
                            ) : (
                              <CancelIcon
                                data-testid='DeliveroptionsErrorIcon'
                                color='error'
                                className={classes.icon}
                                style={{ width: '3%' }}
                              />
                            )}
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: value === 'Fail' ? '#dc3545' : '#28a745',
                                paddingTop: 0.5,
                                paddingBottom: 0.5,
                                paddingLeft: 2,
                                paddingRight: 2
                              }}
                              style={{ width: '97%' }}
                            >
                              {value === 'Success' ? t('AppLabels.deliveryOptionsUpdatedInERP') : t('AppLabels.deliveryOptionsNotUpdated')}
                            </Typography>
                          </Box>
                        </Box>
                      )
                    } else if (key === 'Packageexists') {
                      return (
                        <Box key={key} className={classes.sectionBox}>
                          <Box
                            style={{
                              display: 'flex',
                              width: '100%',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              borderRadius: '5px'
                            }}
                          >
                            {value === 'Success' ? (
                              <CheckCircleIcon color='success' className={classes.icon} style={{ width: '3%' }} />
                            ) : (
                              <CancelIcon
                                data-testid='PackageexistsErrorIcon'
                                color='error'
                                className={classes.icon}
                                style={{ width: '3%' }}
                              />
                            )}
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: value === 'Fail' ? '#dc3545' : '#28a745',
                                paddingTop: 0.5,
                                paddingBottom: 0.5,
                                paddingLeft: 2,
                                paddingRight: 2
                              }}
                              style={{ width: '97%' }}
                            >
                              {value === 'Success' ? t('AppLabels.packageNotSubscribed') : t('AppLabels.packageAlreadySubscribed')}
                            </Typography>
                          </Box>
                        </Box>
                      )
                    }
                    return null
                  })
                : null}
            </div>
          </Container>
        </main>
      </div>
    </ThemeProvider>
  )
}

export default PreCondition
